// VARIOUS VUE FILTERS AND HELPER FUNCTIONS

import { Duration, DateTime } from 'luxon';
import isEqualWith from 'lodash/isEqualWith';
import xor from 'lodash/xor';
import isNil from 'lodash/isNil';
// import toFinite from 'lodash/toFinite';
import toNumber from 'lodash/toNumber';
import { customAlphabet } from 'nanoid';  // can also import { nanoid }

// generator for primary keys on frontend 
const lowerAlphabet = 'abcdefghijklmnopqrstuvwxyz';
const upperAlphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const digitsAlphabet = '1234567890';
const splCustomAlphabet = lowerAlphabet + upperAlphabet + digitsAlphabet;
const random22id = customAlphabet(splCustomAlphabet, 22);
// import and use like this:  
// import { random22id } from './js/filters-helpers';
// myId = random22id()   // => 'some22charString'

import * as dtUtils from './utils/dtUtils';


const hasValueOrZero = function(value) {  // RETURNS BOOLEAN 
    // DRY utility to check if !!value OR value === 0 - works with any type of value
    // use this with ag-grid and other places where you need zero to be a valid value (like validation checks, etc)
    return !!value || value === 0;
}

// TODO - likely should replace toNumber with parseDecimal or similar (uses @international/numbers)
const toNumberOrNull = function(value) {
    // coerce value to a Number if possible, otherwise return null
    if (isNil(value) || value === '') {  // checks for null or undefined or empty string '' - why check empty string here?  we need to be able to clear a cell to no value
        return null;
    } else if (Number.isFinite(value)) {  // if its already a number, just return it
        return value;
    } else {  // if here, its likely a string, so coerce it, check result, and return accordingly
        let numberOrNaN = toNumber(value);  // lodash toNumber() coerces things to a finite number, or NaN.  so must check if NaN afterwards
        return Number.isNaN(numberOrNaN) ? null : numberOrNaN;  // return null instead of NaN... or the number
        // previously used lodash toFinite() but that coerces things to zero instead of NaN
    }
}


// declare these here as local (non-global) Vue filters
// then if you want to make them global, make a separate 'global-filters.js' file and handle them like you do global components

var FormatFileSizeHelper = function(sizeValue) {
    // function to format the filesize to something nice
    let size = sizeValue;
    // #2**10 = 1024
    const power = 2**10;
    let n = 0;
    let powerN = {0 : 'B', 1: 'KB', 2: 'MB', 3: 'GB', 4: 'TB'};
    while (size > power) {
        size /=  power
        n += 1
    }
    size = Math.round(size * 10) / 10;
  
    return size.toString() + ' ' + powerN[n];
};

var ParseFileSizeHelper = function(sizeValue) {
    // function to parse the filesize from a string to a number of bytes
    // can be a number, a number as string (assume that is just # of bytes), or a string like '2.5 MB', '2.5mb', etc.
    // ideally you have a single space between the number and the unit, but we can handle it without a space too
    // returns a number of bytes (as a number) or null if it can't parse it
    if (typeof sizeValue === 'number') {
        return sizeValue;
    }
    if (typeof sizeValue !== 'string') {
        console.error(`ParseFileSizeHelper: invalid value (${sizeValue}) - must be a string or number`);
        return null;
    }  // now we know we have a string
    const sizeString = sizeValue.trim();  // prep it by trimming whitespace
    let size;
    let unit;
    const powerN = {'B': 0, 'KB': 1, 'MB': 2, 'GB': 3, 'TB': 4};
    const unitsUpper = Object.keys(powerN).filter(u => u.length === 2);  // only get the 2-letter units, otherwise 'B' will confuse things
    //const unitsLower = unitsUpper.map(u => u.toLowerCase());  // no 'b' -- only the 2-letter units
    if (sizeString.includes(' ')) {
        // if we have a space in there, assume its a string with a number and a unit
        let sizeParts = sizeString.split(' ');
        if (sizeParts.length !== 2) {
            console.error(`ParseFileSizeHelper: invalid sizeString (${sizeString}) - must have exactly one space separating the number and the unit`);
            return null;
        }
        size = parseFloat(sizeParts[0]);
        unit = sizeParts[1].toUpperCase();
    } else if (sizeString.includes('B') || sizeString.includes('b')) {
        // if we have a 'B' or 'b' in there, assume its a string with a number and a unit, but no space... we'll find the unit and parse it out
        let sizeParts = sizeString.split(/([a-zA-Z]+)/);  // split on the unit, but keep the unit in the array -- there will likely also be a final empty string in the array, we can ignore it
        try {
            size = parseFloat(sizeParts[0]);
            unit = sizeParts[1].toUpperCase();
        } catch (error) {
            console.error(`Got error trying to parse size and/or unit from ${sizeString}`, error);
            return null;
        }
    } else {
        // if no space, no unit detected in the string, assume it is just a number (as a string) - parse that as size and set unit to 'B'
        size = parseFloat(sizeString);
        unit = 'B';
    }
    // we should now have size and unit set -- if not, return null
    if (!size || !unit) {
        // if we parsed a size of 0, we will fail here... but we should not have a size of 0
        console.error(`ParseFileSizeHelper: invalid sizeString (${sizeString}) - could not parse size and/or unit`);
        return null;
    }
    if (!Object.hasOwn(powerN, unit)) {
        console.error(`ParseFileSizeHelper: invalid unit (${unit}) - must be one of ${unitsUpper}`);
        return null;
    }
    const sizeAsFloat = size * (2**10)**powerN[unit];  // still a float... we want to work with integers here
    return Math.round(sizeAsFloat);
};

var FormatDecimalHelper = function(value, maxDigits = 1) {
    return new Intl.NumberFormat('en-US', { style: 'decimal', maximumFractionDigits: maxDigits }).format(value);
};

var FormatPercentHelper = function(value, maxDigits = 1) {
    return new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: maxDigits }).format(value);
};


// DATES / TIMES
// -----------------------------------------------------------------

var FormatDateTimeHelper = function(value) {
    // this uses UTC as timezone - likely NOT what you want going forward - don't change that unless you know it doesn't affect all uses....
    // most likely you should use FormatDateTimeLocal instead
    if (!value) {
        return '';
    }
    return new Date(value).toLocaleString('en-US', { timeZone: 'UTC' });
};

var FormatDateHelper = function(value) {
    // only returns the DATE string, no time
    // WARNING - IF value HAS TIME + TIMEZONE, THEN WHEN WE PARSE USING LUXON, THOSE WILL GET USED, 
    //      BUT toLocaleString() might return a different date, based on TZ of current user!!!
    //      but it works OK if you only pass in an ISO date string, like '2022-09-22' -- then parse/format sequence will not change the dates (luxon will use a 'local' timezone, not UTC when parsing)
    if (!value) {
        return value;  // we've been returning null, but change to return whatever falsey value came in
    }
    const luxonDateTime = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
    if (!luxonDateTime.isValid) return '';
    return luxonDateTime.toLocaleString(DateTime.DATE_SHORT)  // only the date here
};

var FormatTimeHelper = function(value, withSeconds) {
    // returns ONLY TIME for a JS Date object - value should be a datetime string, parsed by Date(value)
    if (!value) {
        return null;
    }
    var options = {
        hour: 'numeric', 
        minute: 'numeric',
    };
    if (withSeconds) {
        options['second'] = 'numeric';
    }
    return new Date(value).toLocaleTimeString([], options);
}

var FormatDateTimeLocal = function(value) {
    if (!value) return '';
    // nov 19 2021 - updated to check if already a DateTime 
    const luxonDateTime = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
    return luxonDateTime.toLocaleString(DateTime.DATETIME_SHORT);
}

const FormatTimeFromIso = function(timeValue) {
    // takes an ONLY time string like '09:45:15' and returns a locale formatted string - uses Luxon
    // returns using 'TIME_SIMPLE' format string -- en_US: 1:30 PM  or french: 13:30
    // don't use this for anything time-zone aware since you don't include zone in output (and because there are no dates)
    // nov 19 2021 - updated to check if already a DateTime
    const luxonDateTime = DateTime.isDateTime(timeValue) ? timeValue : DateTime.fromISO(timeValue);
    if (!luxonDateTime.isValid) return '';  // or should we return the original value?
    return luxonDateTime.toLocaleString(DateTime.TIME_SIMPLE);  // do we need a format string?
}

var HumanizeDateTimeHelper = function(isoString) {
    // use Luxon to return humanized string of relative time to now
    // nov 19 2021 - updated to check if already a DateTime
    if (!isoString) return null;
    let valueDateTime = DateTime.isDateTime(isoString) ? isoString : DateTime.fromISO(isoString);
    let timeDeltaSeconds = valueDateTime.diffNow().as('seconds');  // difference between value DateTime and now, in seconds (these will be NEGATIVE NUMBERS)
    //console.log(timeDeltaSeconds);
    if (timeDeltaSeconds > -60 && timeDeltaSeconds <= 0) {
        return 'Just now';  // if it was less than 60 seconds ago, just say "just now"
    }
    return valueDateTime.toRelative();  // otherwise return the relative string
}

var DateApproachingDeadline = function(deadlineString, durationObject) {
    // use Luxon to return BOOLEAN for whether current Date/Time is within certain window(duration) of a deadline/date (deadline should be ISO string)
    // durationObject should be something like this: { days: 3, hours: 6}
    // return TRUE if we are PAST (greater than) the approaching window start time
    // nov 19 2021 - updated to check if already a DateTime 
    let deadline = DateTime.isDateTime(deadlineString) ? deadlineString : DateTime.fromISO(deadlineString);
    let duration = Duration.fromObject(durationObject);
    let now = DateTime.local();
    let startWindow = deadline.minus(duration);
    return now > startWindow;

}

const isFutureDate = function(isoString) {
    // return Boolean, true if isoString date is in the future compared to NOW
    // returns null if not valid date
    // nov 19 2021 - updated to check if already a DateTime 
    if (!isoString) return null;
    let dateToCheck = DateTime.isDateTime(isoString) ? isoString : DateTime.fromISO(isoString);
    if (!dateToCheck.isValid) return null;
    let now = DateTime.now();
    return dateToCheck > now;
}



const toLuxonDateTime = function(value) {
    // force a DateTime or ISO string to a Luxon DateTime object
    // can take either string or DateTime or JS Date - if not valid return null?
    if (!value) return value;  // return whatever falsy value it was
    if (value instanceof Date) {
        return DateTime.fromJSDate(value);
    }
    return DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
}

const DateTimetoISO = function(value) {
    // force a Luxon DateTime to ISO string, or if already a string, return that
    // if a string, should we validate its a valid ISO string by converting to DateTime and validating first???
    if (!value) return value;
    return DateTime.isDateTime(value) ? value.toISO() : value;
}


const getDateSlug = () => {
    // return a STRING of 'YYYYMMDD' based on current user local date/time
    // includes year, month, day -- all padded to 2 (except year to 4)
    const luxonLocal = DateTime.local();
    return luxonLocal.toFormat('yyyyMMdd');
}

const getDateTimeSlug = () => {
    // return a STRING of 'YYYYMMDDHHMMSS' based on current user local date/time
    // includes year, month, day, hour, minutes, seconds -- all padded to 2 (except year to 4)
    const luxonLocal = DateTime.local();
    return luxonLocal.toFormat('yyyyMMddHHmmss');
}




const luxonDurationToHumanizedString = function(luxonDuration) {
    // a helper used by other helpers... takes a luxonDuration object, outputs a humanized string in best representation we can find
    // if not valid, returns empty string
    if (!luxonDuration || !luxonDuration.isValid) {
        return ''
    }
    const durationObj = luxonDuration.toObject();
    // durationObj will be a JS object such as {days: 2, hours: 12} 
    let humanizedArray = [];
    Object.entries(durationObj).forEach(([period, number]) => {  // period is something like 'weeks' and number is the number of them
        // we may have added in many different periods in making luxonDuration (so it rebalances to those periods) - so when outputting, only include periods with non-zero numbers
        if (number !== 0) {
            humanizedArray.push(`${number} ${period}`);
        }
    });
    return humanizedArray.join(', ');
}

const HumanizeDurationFromIsoHelper = function(isoString) {  
    // takes ISO duration string (P5W3D) and outputs a human-friendly STRING like '5 weeks, 3 days'
    if (!isoString) return '';
    let luxonDuration = Duration.fromISO(isoString).normalize();  // make the luxonDuration and normalize it to rebalance the units
    return luxonDurationToHumanizedString(luxonDuration);
}

// this below is not a filter, but still a nice reusable helper function
var FormatDurationFromIsoHelper = function(isoValue, unitString) {  
    // returns a NUMBER or null if no isoValue or if its not a valid duration
    // must specify the units on this one -- see above for one where no need to specify units (that one is better)
    // isoValue should be a String date/time in ISO format
    // unitString should be something like 'days' or 'months' or 'years' or 'hours' or 'minutes'
    if (!isoValue) {
        return null;
    }
    let luxonDuration = Duration.fromISO(isoValue);
    if (!luxonDuration.isValid) {
        return null;
    }
    return luxonDuration.as(unitString);
};

const DurationIsoToMinutes = function(isoValue) {
    // converts ISO duration string ('P5W') to a NUMBER of minutes using Luxon, so you can enable sorting of durations
    // returns null if no value, or if its invalid duration
    return FormatDurationFromIsoHelper(isoValue, 'minutes');
}

const DurationSecondsToHumanized = function(numSeconds) {
    // converts Number of seconds to a humanized string
    // in formbuilder, django returns durations as number of seconds... and we want to show a humanized version
    if (numSeconds === null || numSeconds === undefined) {
        return '';
    }
    const numNum = Number(numSeconds);
    if (Number.isNaN(numNum)) {
        return '';
    }
    // Round the seconds to nearest integer
    const roundedSeconds = Math.round(numNum);
    
    // rebalance to logical units with normalize, add in many extra units when we create it so it rebalances to those
    // it only rebalances to the keys included - so if you only have days/seconds, you'll only have that in output 
    const luxonDuration = Duration.fromObject({days: 0, hours: 0, minutes: 0, seconds: roundedSeconds}).normalize();
    return luxonDurationToHumanizedString(luxonDuration);
}

// NOTE: this is quite old, you have new stuff in other utils file now
var FormatCurrencyHelper = function(value, currencyCode) {
    //console.log('From start of FormatCurrencyHelper, dumping value');
    //console.log(value);
    let numPattern = /[^0-9.-]+/g;
    let curCode = currencyCode || 'USD';  // use USD as fallback
    const currencyFormat = {
        style: 'currency',
        currency: curCode,
    };
    const theLocale = 'en-US';  // assume this for now
    if (!Number.isFinite(value)) {  // be careful not to reject zero 0 here -- you want 0 amounts to pass through as valid values
        // zero will not get rejected here, but null, '' will get rejected
        return null;
    }
    // numValueRaw will be Number, but may have too many digits/decimal places
    let valueIsNumber = typeof value === 'number'
    let valueAsNumber = valueIsNumber ? value : value.replace(numPattern, '');
    var numValueRaw = Number.parseFloat(valueAsNumber);
    if (Number.isNaN(numValueRaw)) {
        return null;
    } else {
        // use .toFixed to truncate decimals, returns a string, then parse back to a number
        // let truncated = Number.parseFloat(numValueRaw.toFixed(this.localDecimals));
        // skip the decimal truncation for this... since we never passed in decimals
        return numValueRaw.toLocaleString(theLocale, currencyFormat);
    }
};

var FormatCapitalizeHelper = function(value) {
    // capitalizes first letter of sentence or word
    if (typeof value !== 'string') return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Converts integer to a hexidecimal code, prepad's single 
 * digit hex codes with 0 to always return a two digit code. 
 * 
 * @param {Integer} i Integer to convert 
 * @returns {String} The hexidecimal code
 */
function intToHex(i) {
    var hex = parseInt(i).toString(16);
    return (hex.length < 2) ? "0" + hex : hex;
}

/**
 * Return hex color from scalar *value*.
 *
 * @param {float} value Scalar value between 0 and 1
 * @return {String} color
 */
function makeColorHelper(value) {
    // value must be between [0, 510]
    value = Math.min(Math.max(0,value), 1) * 510;

    var redValue;
    var greenValue;
    if (value < 255) {
        redValue = 255;
        greenValue = Math.sqrt(value) * 16;
        greenValue = Math.round(greenValue);
    } else {
        greenValue = 255;
        value = value - 255;
        redValue = 256 - (value * value / 255)
        redValue = Math.round(redValue);
    }

    return "#" + intToHex(redValue) + intToHex(greenValue) + "00";
}


var errorMessageMaker = function(content) {
    // used to take returned error objects and output them to array of strings, then a single string
    let outputArray = [];
    if (typeof content === 'string') {
        outputArray = [content];
    } else if (Object.keys(content).length) {  // check if content has keys (is an object, really)
        for (const [key, value] of Object.entries(content)) {
            let keysToRemove = ['detail', 'error', 'context', 'kwargs'];  // TODO - add more here -- for these keys, we'll remove the key itself, but still show the value
            let kvString = keysToRemove.includes(key) ? `${value}` : `${key}: ${value}`;
            outputArray.push(kvString);
        }
    } else if (Array.isArray(content)) {
        outputArray = [...content];  // just make a copy
    } else {  // not sure what would go here.... we caught string first
        outputArray = [content];
    }
    // from here you could just return outputArray as an array
    // now convert to a single string -- PUT NEWLINES IN BETWEEN?
    let outputString = '';
    if (Array.isArray(outputArray)) {
        outputString = outputArray.join('\n');
    } else {
        outputString = outputArray;
    }
    return outputString;
}

function isSameArray(first, second) {
    // our own method built on lodash to compare 2 arrays and return if they're the same, just different orders
    // other similar methods below are for comparing objects with arrays in them
    if (Array.isArray(first) && Array.isArray(second)) {
        return xor(first, second).length === 0;  // if there are no exclusive values between 2 arrays, then they're same, so return true
    }
    return false;  // if the if statement failed, then something is not right... you should always pass in arrays to this, even if empty
}


function sameArrayElements(firstItem, secondItem) {
    // our customizer function for isEqualAnyOrder below -- we don't export this
    // don't return anything unless you know result is true or false... isEqualWith expects that
    // the values passed to this will be the individual items (keys/values of objects) of the things being checked
    if (Array.isArray(firstItem) && Array.isArray(secondItem)) {
        return xor(firstItem, secondItem).length === 0;  // if there are no exclusive values between 2 arrays, then they're same, so return true
    }
}

function isEqualAnyOrder(first, second) {
    // does isEqual from lodash (isEqualWith), but if items in object are array, it ignores the order
    return isEqualWith(first, second, sameArrayElements);  // sameArrayElements is our customizer
}

function isNumberInRange(value, min, max) {
    // return Boolean based on whether value is inside min and max (INCLUSIVE on both ends) 
    // used for checking if in lat/lng ranges mostly
    // accepts Numbers or Strings
    // you could also use lodash inRange - that is not inclusive 
    // weed out any null or '' first -- otherwise these return 0 if coerced to Number()
    const invalidValues = [null, undefined, '', NaN];  // checking NaN === NaN returns false, but it works inside array.includes()
    if (invalidValues.includes(value)) {
        return false;
    }
    // now make sure we can coerce it to a number
    const valueNum = Number(value);
    if (Number.isNaN(valueNum)) {
        return false;
    }
    if (valueNum > max || valueNum < min) {  // if OUTSIDE of range, then false
        return false;
    }
    return true;  // if we're still alive, then we're valid
}

function isValidLatitude(lat) {
    return isNumberInRange(lat, -90, 90);
}

function isValidLongitude(lng) {
    return isNumberInRange(lng, -180, 180);
}

function stadiaMapStaticImgSrc({
    style = 'osm_bright',  // options are 'osm_bright' 'alidade_smooth' and others
    lat,  // REQUIRED
    lng,  // REQUIRED
    zoom = '4',  // 1-18
    markers,  // Array of 2-item Arrays [[lat,lng], [lat,lng]] - if not provided, we'll put a single marker at lat, lng 
    width = '400',  // width of image in points - gets multiplied by image_scale if its provided
    height = '300',  // height of image in points - gets multiplied by image_scale if its provided
    image_scale = '@2x', // they recommend '@2x'
}) {
    if (!isValidLatitude(lat) || !isValidLongitude(lng)) {
        return '';  // return empty string if you don't have valid things
    }
    // https://stadiamaps.com/static/<style>?api_key=<key>&center=<lat>,<lng>&zoom=<zoom>[&markers=lat,lng,[style_variant],[color],[label]][&manual_attribution=true]&size=<width>x<height>[@2x]
    // api_key is only needed for mobile apps, not for web (because they look at hostname)
    const baseStadiaUrl = `https://stadiamaps.com/static/${style}`;
    const latLngString = `${lat},${lng}`;
    const markersArray = (!!markers && Array.isArray(markers) && markers.every(mPair => Array.isArray(mPair))) ? markers : [[lat, lng]];  // make sure you have a 2D array of markers
    const markersString = markersArray.map(mPair => mPair.join(',')).join('|');  // first make a comma separated pair, then a pipe-separated string if you have multiple
    const sizeString = `${width}x${height}${image_scale ? image_scale : ''}`;
    const apiKey = '';  // not needed as long as you have domain auth working -- must add referrerpolicy="strict-origin" or "origin" to <img> tag
    const apiKeyQs = apiKey ? `api_key=${apiKey}&` : '';
    let urlParams = new URLSearchParams();  // don't use urlParams... it encodes the special characters that stadia wants
    urlParams.append('api_key', apiKey);
    urlParams.append('center', latLngString);
    urlParams.append('zoom', zoom);
    urlParams.append('markers', markersString);
    urlParams.append('size', sizeString);
    //return baseStadiaUrl + '?' + urlParams.toString();  // this encodes special characters, which is not what we want
    return `${baseStadiaUrl}?${apiKeyQs}center=${latLngString}&zoom=${zoom}&markers=${markersString}&size=${sizeString}`;
}


export {
    random22id,
    hasValueOrZero,
    toNumberOrNull,
    FormatFileSizeHelper,
    ParseFileSizeHelper,
    FormatDateHelper,
    FormatTimeHelper,
    FormatTimeFromIso,
    getDateSlug,
    getDateTimeSlug,
    FormatDurationFromIsoHelper,
    DurationIsoToMinutes,
    DurationSecondsToHumanized,
    luxonDurationToHumanizedString,
    FormatCurrencyHelper,
    FormatDateTimeHelper,
    makeColorHelper,
    FormatDecimalHelper,
    FormatPercentHelper,
    FormatCapitalizeHelper,
    errorMessageMaker,
    HumanizeDateTimeHelper,
    HumanizeDurationFromIsoHelper,
    FormatDateTimeLocal,
    DateApproachingDeadline,
    isEqualAnyOrder,
    isSameArray,
    stadiaMapStaticImgSrc,
    isFutureDate,
    DateTimetoISO,
    toLuxonDateTime,
    // new dtUtils
    dtUtils,
}